import { Component } from '@theme/component';

/**
 * Offline store locator
 *
 * Client-side filtering over server-rendered store cards. Every store is rendered up
 * front; this component only toggles visibility and keeps the heading in step.
 *
 * State lives on the instance, so two sections on one page never collide.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} [search]
 * @property {HTMLButtonElement[]} [stateButtons]
 * @property {HTMLElement[]} [cards]
 * @property {HTMLElement} [heading]
 * @property {HTMLElement} [count]
 * @property {HTMLElement} [empty]
 *
 * @extends {Component<Refs>}
 */
class OfflineStoreLocations extends Component {
  /** @type {{ query: string, state: string }} */
  #state = { query: '', state: '' };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('submit', this.#handleSubmit);

    // Default to the first region so the results column is never empty on load.
    this.#state.state = this.#stateButtons[0]?.dataset.state ?? '';
    this.#apply();
  }

  get #cards() {
    return this.refs.cards ?? [];
  }

  get #stateButtons() {
    return this.refs.stateButtons ?? [];
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-state]');
    if (button instanceof HTMLButtonElement) {
      // Picking a region clears the search, otherwise the two would fight.
      this.#state.state = button.dataset.state ?? '';
      this.#state.query = '';
      if (this.refs.search) this.refs.search.value = '';
      this.#apply();
    }
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    if (event.target !== this.refs.search) return;
    this.#state.query = this.refs.search?.value.trim().toLowerCase() ?? '';
    this.#apply();
  };

  /** The search row is a form so Enter submits; there is nothing to post. */
  /** @param {Event} event */
  #handleSubmit = (event) => {
    event.preventDefault();
    this.#apply();
  };

  #apply() {
    const { query, state } = this.#state;
    const searching = query !== '';
    let visible = 0;

    for (const card of this.#cards) {
      // While searching, matches are shown across every region.
      const show = searching
        ? (card.dataset.search ?? '').includes(query)
        : card.dataset.state === state;

      card.hidden = !show;
      if (show) visible += 1;
    }

    for (const button of this.#stateButtons) {
      const isActive = !searching && button.dataset.state === state;
      button.classList.toggle('locations__state--active', isActive);
      button.setAttribute('aria-current', String(isActive));
    }

    if (this.refs.heading) {
      this.refs.heading.textContent = searching
        ? (this.dataset.labelResults ?? 'Search results')
        : state;
    }

    if (this.refs.count) {
      const noun = visible === 1 ? this.dataset.labelStoreSingular : this.dataset.labelStorePlural;
      this.refs.count.textContent = `${visible} ${noun ?? ''}`.trim();
      this.refs.count.hidden = !searching;
    }

    if (this.refs.empty) this.refs.empty.hidden = visible > 0;
  }
}

if (!customElements.get('offline-store-locations')) {
  customElements.define('offline-store-locations', OfflineStoreLocations);
}
